import React, { MouseEventHandler, ReactNode } from "react";
import AvatarComp from "../Avatar";
import Dropdown from "../Dropdown";
import Notifications from "./Notifications";
import Icon, { ICON_TYPE_BELL_FILLED } from "../Icon";
import withTheme from "../../hocs/withTheme";
import { createUniqueKey } from "../../utils";
import { useActiveSystemMessage } from "prodivers/ActiveSystemMessageProvider";
import { getSetupData } from "additiv-services/setup";
import "./ProfileMenu.css";


const setupData = getSetupData();

type ProfileMenuProps = {
    image?: string;
    name?: string;
    email?: string;
    additionalInfo?: ReactNode;
    footer?: ReactNode;
    editText?: string;
    logoutText?: string;
    withEdit?: boolean;
    withAlert?: boolean;
    onEdit?: MouseEventHandler<HTMLButtonElement> | undefined;
    onLogout?: MouseEventHandler<HTMLButtonElement> | undefined;
    notifications?: {
        data?: {
            subject?: string;
            date?: string;
            text?: string | object;
        }[];
        onReadNotification?: () => void;
        errorMessage?: string;
        emptyMessage?: string;
    };
    withAdditionalButtons?: {
        onClick?: MouseEventHandler<HTMLButtonElement>;
        text?: string;
        id?: string | undefined;
    }[];
};

function ProfileMenu({
    image = '',
    name = undefined,
    email = undefined,
    editText = 'Edit profile',
    onEdit = () => {},
    logoutText = 'Logout',
    onLogout = () => {},
    withEdit = false,
    withAlert = false,
    notifications = {
        data: [],
        onReadNotification: () => {},
        errorMessage: '',
        emptyMessage: '',
    },
    additionalInfo = null,
    footer = null,
    withAdditionalButtons = [],
    ...rest
} : ProfileMenuProps) {

    const isNotificationsEmpty =
        !!notifications?.errorMessage ||
        (Array.isArray(notifications?.data) && notifications.data.length === 0);

    const Avatar = image ? (
        <AvatarComp src={image} className="avatar" role="button" tabIndex={0} />
    ) : (
        <AvatarComp className="avatar" role="button" tabIndex={0} name={name} />
    );

    const { 
        hasActiveSystemMessage, 
        isPreviewActive, 
        setPreviewMessageStatus,
        isLoading,
    } = useActiveSystemMessage();

    const toggleSystemMessagePreview = () => {
        if (hasActiveSystemMessage) {
            setPreviewMessageStatus?.(!isPreviewActive);
        }   
    }
    
    return (
        <div className="profile-menu">
            {!setupData.SYSTEM_MESSAGE_FEATURE_FLAG && withAlert && (
                <Dropdown
                    placement="bottomRight"
                    overlayClassName="notifications-menu__Popover"
                    title={
                        <Icon
                            className={`notifications-menubutton ${
                                isNotificationsEmpty
                                    ? "notifications-menubutton--empty"
                                    : ""
                            }`}
                            type={ICON_TYPE_BELL_FILLED}
                            role="button"
                            tabIndex={0}
                        />
                    }
                >
                    <div className="profile-menu__notification">
                        <Notifications {...notifications} />
                    </div>
                </Dropdown>
            )}
            {setupData.SYSTEM_MESSAGE_FEATURE_FLAG && <Icon
                className={`notifications-menubutton ${
                    !hasActiveSystemMessage || isLoading
                        ? "notifications-menubutton--empty notifications-menubutton--disabled"
                        : ""
                }`}
                type={ICON_TYPE_BELL_FILLED}
                role="button"
                tabIndex={0}
                onClick={toggleSystemMessagePreview}
            />}
            <Dropdown
                placement="bottomRight"
                title={Avatar}
                {...rest}
                overlayClassName="profile-menu__Popover"
            >
                <li className="ProfileMenuWrapper" aria-label={"Options"}>
                    <ul className={"ProfileMenu"}>
                        <li className={"ProfileMenu__Item"}>
                            <div className={"ProfileMenu__TextWrapper"}>
                                <span className={"ProfileMenu__Text"}>
                                    {name}
                                </span>
                            </div>
                        </li>

                        {withEdit && (
                            <li className={"ProfileMenu__Item"}>
                                <button
                                    className={"ProfileMenu__Button"}
                                    type={"button"}
                                    onClick={onEdit}
                                >
                                    {editText}
                                </button>
                            </li>
                        )}

                        {withAdditionalButtons?.length > 0 && (
                            <li role={"separator"} />
                        )}

                        {withAdditionalButtons?.length > 0 &&
                            withAdditionalButtons.map((button, index) => (
                                <li
                                    className="ProfileMenu__Item"
                                    key={createUniqueKey(index, button?.text)}
                                >
                                    <button
                                        className={"ProfileMenu__Button"}
                                        type={"button"}
                                        id={button?.id}
                                        onClick={button?.onClick}
                                    >
                                        {button?.text}
                                    </button>
                                </li>
                            ))}

                        <li role={"separator"} />

                        <li className={"ProfileMenu__Item"}>
                            <button
                                className={"ProfileMenu__Button"}
                                type="button"
                                onClick={onLogout}
                            >
                                {logoutText}
                            </button>
                        </li>
                    </ul>
                </li>
            </Dropdown>
        </div>
    );
}

export default withTheme(ProfileMenu);
